// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: HTML pages and Markdown documents shown in place (design/html-and-subagents h*, md*).
// Which files count, the locked-down page an HTML preview renders, and what a Markdown document's
// outline and checklists are.

import {type CellAlign, delimiterRow, tableCells} from '@app/features/document_preview/MarkdownTable';

export type DocumentKind = 'html' | 'markdown';

/** Largest file Konvo renders in place; bigger ones stay a plain file row. */
export const DOCUMENT_PREVIEW_MAX_BYTES = 2 * 1024 * 1024;

const HTML_EXTENSIONS = /\.(html?|xhtml)$/i;
const MARKDOWN_EXTENSIONS = /\.(md|markdown|mdown|mkd)$/i;

export function documentKind(name: string | null | undefined, contentType?: string | null): DocumentKind | null {
	const type = (contentType ?? '').split(';')[0].trim().toLowerCase();
	if (type === 'text/html' || type === 'application/xhtml+xml') return 'html';
	if (type === 'text/markdown' || type === 'text/x-markdown') return 'markdown';
	const file = name ?? '';
	if (HTML_EXTENSIONS.test(file)) return 'html';
	if (MARKDOWN_EXTENSIONS.test(file)) return 'markdown';
	return null;
}

// Shared interactive HTML policy; callers must use an opaque-origin sandbox.
export {htmlPreviewDocument} from '@app/features/messaging/utils/HtmlPreviewDocument';

/** The page's own title, else null. */
export function htmlTitle(html: string): string | null {
	const match = /<title[^>]*>([\s\S]*?)<\/title>/i.exec(html);
	const title = match?.[1]
		?.replace(/&amp;/g, '&')
		.replace(/&lt;/g, '<')
		.replace(/&gt;/g, '>')
		.replace(/&#39;|&apos;/g, "'")
		.replace(/&quot;/g, '"')
		.replace(/\s+/g, ' ')
		.trim();
	return title ? title.slice(0, 200) : null;
}

export interface MarkdownHeading {
	level: number;
	text: string;
	/** Which heading this is, counting from 0, to scroll to it in the rendered document. */
	index: number;
}

/** Strips inline Markdown from a heading so the outline reads as plain text. */
const plainHeading = (text: string) =>
	text
		.replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
		.replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
		.replace(/[*_`~]/g, '')
		.replace(/\s+#+\s*$/, '')
		.trim();

/** Every #, ## and ### heading outside code blocks, in order. */
export function markdownHeadings(markdown: string): Array<MarkdownHeading> {
	const out: Array<MarkdownHeading> = [];
	let fence: string | null = null;
	let index = 0;
	for (const line of markdown.split('\n')) {
		const fenceMatch = /^\s{0,3}(`{3,}|~{3,})/.exec(line);
		if (fenceMatch) {
			if (fence === null) fence = fenceMatch[1][0];
			else if (fenceMatch[1][0] === fence) fence = null;
			continue;
		}
		if (fence !== null) continue;
		const heading = /^\s{0,3}(#{1,6})\s+(.+)$/.exec(line);
		if (!heading) continue;
		const level = heading[1].length;
		const text = plainHeading(heading[2]);
		if (text && level <= 3) out.push({level, text, index});
		index += 1;
	}
	return out;
}

/** The document's title: its first heading, else null. */
export function markdownTitle(markdown: string): string | null {
	const first = markdownHeadings(markdown)[0];
	return first ? first.text.slice(0, 200) : null;
}

/** Ticked and total task-list items ("- [x] Done"), outside code blocks. */
export function markdownTasks(markdown: string): {done: number; total: number} {
	let done = 0;
	let total = 0;
	let fence = false;
	for (const line of markdown.split('\n')) {
		if (/^\s{0,3}(`{3,}|~{3,})/.test(line)) fence = !fence;
		if (fence) continue;
		const task = /^\s*[-*+]\s+\[([ xX])\]\s+/.exec(line);
		if (!task) continue;
		total += 1;
		if (task[1] !== ' ') done += 1;
	}
	return {done, total};
}

/**
 * A picture a Markdown document shows (`![alt](src)`, `<img src width height>`), and where it links
 * to (`[![alt](src)](href)`, `<a href><img></a>`).
 */
export interface DocumentPicture {
	src: string;
	alt: string;
	href?: string;
	width?: number;
	height?: number;
}

/** A Markdown document as blocks Konvo's (chat-style) Markdown can draw, plus what it can't. */
export type DocumentBlock =
	| {kind: 'text'; text: string}
	| {kind: 'rule'}
	| {kind: 'tasks'; items: Array<{done: boolean; text: string; depth: number}>}
	| {kind: 'facts'; rows: Array<[string, string]>}
	| {kind: 'pictures'; items: Array<DocumentPicture>}
	| {kind: 'html'; html: string}
	/** A table with pictures in its cells (before / after screenshots); each cell as written. */
	| {kind: 'table'; align: Array<CellAlign>; header: Array<string>; rows: Array<Array<string>>}
	/** A folded section (`<details><summary>Screenshots</summary> … </details>`), its inside as blocks. */
	| {kind: 'details'; summary: string; open: boolean; blocks: Array<DocumentBlock>}
	/** A video the document's source knows, on a line of its own (as GitHub writes a dropped video). */
	| {kind: 'video'; src: string};

/** A size from a width or height attribute, in pixels; else undefined. */
export function pictureSize(value: string | null | undefined): number | undefined {
	const size = Number.parseInt(value ?? '', 10);
	return Number.isFinite(size) && size > 0 && size <= 4000 ? size : undefined;
}

const FENCE_LINE = /^ {0,3}(`{3,}|~{3,})/;
const RULE_LINE = /^ {0,3}([-*_])(?:[ \t]*\1){2,}[ \t]*$/;
const TASK_LINE = /^(\s*)[-*+]\s+\[([ xX])\]\s+(.*)$/;
const HEADING_LINE = /^\s{0,3}(#{1,6})\s+/;
/** Lines that start a block of their own, so they never join the line before. */
const BLOCK_START = /^\s{0,3}(#{1,6}\s|>|[-*+]\s|\d+[.)]\s|\||```|~~~|<\/?[a-z])/i;
/** A picture, maybe inside a link: `![alt](src "title")` or `[![alt](src)](href)`. */
const PICTURE =
	/^(?:\[\s*)?!\[([^\]\n]*)\]\(\s*<?([^)\s>]+)>?(?:\s+"[^"\n]*")?\s*\)(?:\s*\]\(\s*<?([^)\s>]+)>?(?:\s+"[^"\n]*")?\s*\))?/;
/** Tags that start a block of raw HTML, as READMEs use for a centred logo or a row of badges. */
const HTML_BLOCK_TAGS = new Set([
	'a',
	'blockquote',
	'br',
	'center',
	'details',
	'div',
	'figure',
	'h1',
	'h2',
	'h3',
	'h4',
	'h5',
	'h6',
	'hr',
	'img',
	'ol',
	'p',
	'picture',
	'section',
	'summary',
	'table',
	'ul',
]);
const HTML_START = /^ {0,3}<\/?([a-z][a-z0-9]*)(?=[\s/>]|$)/i;

/** A line holding only pictures (a logo, a row of badges), as its pictures; else null. */
export function pictureLine(line: string): Array<DocumentPicture> | null {
	const items: Array<DocumentPicture> = [];
	let rest = line.trim();
	while (rest) {
		const match = PICTURE.exec(rest);
		if (!match) return null;
		const picture: DocumentPicture = {src: match[2], alt: match[1].trim()};
		if (match[3]) picture.href = match[3];
		// A bracket that opens with no link after the picture isn't a linked picture.
		if (rest.startsWith('[') && !match[3]) return null;
		items.push(picture);
		rest = rest.slice(match[0].length).trimStart();
	}
	return items.length ? items : null;
}

const PICTURE_TITLE = String.raw`(?:\s+"[^"\n]*")?\s*\)`;
const MARKDOWN_PICTURE = String.raw`!\[([^\]\n]*)\]\(\s*<?([^)\s>]+)>?${PICTURE_TITLE}`;
/**
 * A picture anywhere in a line: groups 1-3 a linked Markdown picture (alt, src, href), 4-5 a
 * Markdown picture (alt, src), 6-7 a linked `<img>` (the link's and the picture's attributes), 8 an
 * `<img>`'s attributes.
 */
const INLINE_PICTURE = new RegExp(
	String.raw`\[\s*${MARKDOWN_PICTURE}\s*\]\(\s*<?([^)\s>]+)>?${PICTURE_TITLE}` +
		`|${MARKDOWN_PICTURE}` +
		String.raw`|<a\b([^<>]*)>\s*<img\b([^<>]*?)\/?>\s*<\/a>` +
		String.raw`|<img\b([^<>]*?)\/?>`,
	'gi',
);
const ATTRIBUTE = /([a-z][a-z0-9-]*)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+))/gi;

/** The few entities an attribute or summary holds, decoded as a browser would. */
function decodeEntities(text: string): string {
	return text
		.replace(/&lt;/g, '<')
		.replace(/&gt;/g, '>')
		.replace(/&quot;/g, '"')
		.replace(/&#0?39;|&apos;/g, "'")
		.replace(/&nbsp;/g, ' ')
		.replace(/&amp;/g, '&');
}

function attributes(text: string): Map<string, string> {
	const values = new Map<string, string>();
	for (const match of text.matchAll(ATTRIBUTE)) {
		const name = match[1].toLowerCase();
		if (!values.has(name)) values.set(name, decodeEntities(match[2] ?? match[3] ?? match[4] ?? ''));
	}
	return values;
}

function htmlPicture(imageAttributes: string, linkAttributes?: string): DocumentPicture | null {
	const image = attributes(imageAttributes);
	const src = image.get('src')?.trim();
	if (!src) return null;
	const picture: DocumentPicture = {src, alt: image.get('alt')?.trim() ?? ''};
	const width = pictureSize(image.get('width'));
	const height = pictureSize(image.get('height'));
	if (width) picture.width = width;
	if (height) picture.height = height;
	const href = linkAttributes === undefined ? undefined : attributes(linkAttributes).get('href')?.trim();
	if (href) picture.href = href;
	return picture;
}

/** Inline code spans blanked out (same length), so pictures written in them stay text. */
function maskCodeSpans(text: string): string {
	return text.replace(/(`+)[^`]*?\1/g, (span) => ' '.repeat(span.length));
}

export type PicturePart = {kind: 'text'; text: string} | {kind: 'picture'; picture: DocumentPicture};

/** A piece of text (a table cell) as its text and the pictures in it, Markdown or `<img>`, in order. */
export function pictureParts(text: string): Array<PicturePart> {
	const out: Array<PicturePart> = [];
	let rest = 0;
	for (const match of maskCodeSpans(text).matchAll(INLINE_PICTURE)) {
		const picture: DocumentPicture | null =
			match[2] !== undefined
				? {src: match[2], alt: match[1].trim(), ...(match[3] ? {href: match[3]} : {})}
				: match[5] !== undefined
					? {src: match[5], alt: match[4].trim()}
					: match[7] !== undefined
						? htmlPicture(match[7], match[6])
						: htmlPicture(match[8]);
		if (!picture) continue;
		const before = text.slice(rest, match.index);
		if (before.trim()) out.push({kind: 'text', text: before});
		out.push({kind: 'picture', picture});
		rest = match.index + match[0].length;
	}
	const after = text.slice(rest);
	if (after.trim()) out.push({kind: 'text', text: after});
	return out;
}

/** Inline tags that only shape their words (`<sub>`, `<kbd>`, `<span>`): drawn as the words. */
const PLAIN_INLINE = 'sub|sup|small|kbd|span|u|ins|mark|abbr|samp|var|cite|q|font';
const INLINE_HTML: Array<[RegExp, string | ((...groups: Array<string>) => string)]> = [
	[/<br\s*\/?>/gi, '\n'],
	[/<(b|strong)\b[^>]*>([\s\S]*?)<\/\1\s*>/gi, (_, _tag, words) => (words.trim() ? `**${words.trim()}**` : '')],
	[/<(i|em)\b[^>]*>([\s\S]*?)<\/\1\s*>/gi, (_, _tag, words) => (words.trim() ? `*${words.trim()}*` : '')],
	[/<(s|del|strike)\b[^>]*>([\s\S]*?)<\/\1\s*>/gi, (_, _tag, words) => (words.trim() ? `~~${words.trim()}~~` : '')],
	[/<code\b[^>]*>([\s\S]*?)<\/code\s*>/gi, (_, words) => `\`${decodeEntities(words)}\``],
	[
		/<a\b([^>]*)>([\s\S]*?)<\/a\s*>/gi,
		(_, link, words) => {
			const href = attributes(link).get('href')?.trim();
			return href && /^(https?:|mailto:)/i.test(href) ? `[${words}](${href})` : words;
		},
	],
	[new RegExp(`<\\/?(?:${PLAIN_INLINE})\\b[^>]*>`, 'gi'), ''],
];

/**
 * A line of text with the inline HTML GitHub allows turned into the Markdown chat draws (`<b>` to
 * `**`, `<code>` to backticks, `<br>` to a line break; `<sub>`, `<kbd>` and the like to their
 * words), leaving code spans as written. Pictures are lifted out before this (pictureParts).
 */
export function inlineHtmlToMarkdown(text: string): string {
	if (!text.includes('<')) return text;
	const masked = maskCodeSpans(text);
	let out = '';
	let rest = 0;
	// Only the stretches outside code spans change.
	for (const span of masked.matchAll(/ +/g)) {
		if (text.slice(span.index, span.index + span[0].length).trim() === '') continue;
		out += convertInline(text.slice(rest, span.index)) + text.slice(span.index, span.index + span[0].length);
		rest = span.index + span[0].length;
	}
	return out + convertInline(text.slice(rest));
}

function convertInline(text: string): string {
	let out = text;
	for (const [pattern, replacement] of INLINE_HTML)
		out = out.replace(pattern, replacement as (substring: string, ...args: Array<string>) => string);
	return out;
}

const hasPicture = (cell: string) => pictureParts(cell).some((part) => part.kind === 'picture');

/** Tags out and entities decoded, whitespace collapsed: a summary's words. */
function plainText(html: string): string {
	return decodeEntities(html.replace(/<[^>]*>/g, ' '))
		.replace(/\s+/g, ' ')
		.trim();
}

const DETAILS_OPEN = /<details\b[^>]*>/gi;
const DETAILS_CLOSE = /<\/details\s*>/gi;

/** The line that closes the `<details>` opened on line `start` (code blocks skipped), or -1. */
function detailsEnd(lines: Array<string>, start: number): number {
	let depth = 0;
	let fence: string | null = null;
	for (let at = start; at < lines.length; at++) {
		const marker = FENCE_LINE.exec(lines[at])?.[1];
		if (fence) {
			if (marker && marker[0] === fence[0] && marker.length >= fence.length) fence = null;
			continue;
		}
		if (marker) {
			fence = marker;
			continue;
		}
		const line = maskCodeSpans(lines[at]);
		depth += (line.match(DETAILS_OPEN)?.length ?? 0) - (line.match(DETAILS_CLOSE)?.length ?? 0);
		if (depth <= 0) return at;
	}
	return -1;
}

function detailsBlock(text: string, options: BlockOptions): DocumentBlock {
	const open = /<details\b([^>]*)>/i.exec(text);
	let inside = open ? text.slice(open.index + open[0].length) : text;
	const close = inside.toLowerCase().lastIndexOf('</details');
	if (close >= 0) inside = inside.slice(0, close);
	const summary = /^\s*<summary\b[^>]*>([\s\S]*?)<\/summary\s*>/i.exec(inside);
	if (summary) inside = inside.slice(summary[0].length);
	// The blank lines GitHub needs around Markdown inside the section aren't part of it.
	inside = inside.replace(/^(?:[ \t]*\n)+/, '').replace(/(?:\n[ \t]*)+$/, '');
	return {
		kind: 'details',
		summary: summary ? plainText(summary[1]) : '',
		open: /(^|\s)open(\s|=|$)/i.test(open?.[1] ?? ''),
		blocks: blocksOf(inside, options, false),
	};
}

function frontMatter(lines: Array<string>): {rows: Array<[string, string]>; rest: number} | null {
	if (lines[0]?.trim() !== '---') return null;
	const end = lines.findIndex((line, index) => index > 0 && /^(---|\.\.\.)\s*$/.test(line));
	if (end < 0 || end > 60) return null;
	const rows: Array<[string, string]> = [];
	for (const line of lines.slice(1, end)) {
		const pair = /^([A-Za-z0-9_.-]{1,40}):\s*(.*)$/.exec(line);
		if (pair?.[2].trim()) rows.push([pair[1], pair[2].trim().replace(/^["']|["']$/g, '')]);
	}
	return {rows: rows.slice(0, 12), rest: end + 1};
}

/**
 * Splits a document into what Konvo's Markdown draws (headings, lists, tables, quotes, code) and
 * the bits it lacks: front matter, `---` rules and task lists. Hard-wrapped paragraphs are joined
 * (chat Markdown keeps every line break), h5/h6 become h4, lines of pictures and raw HTML blocks
 * (a README's centred logo and badges), tables with pictures in them, folded `<details>` sections
 * and the source's `videos` (an address on a line of its own) are drawn by MarkdownDocument, and
 * every heading keeps its order for the outline. With `keepLineBreaks` (text written in an editor,
 * such as a ClickUp description) every line stays its own and there is no front matter.
 */
export function markdownDocumentBlocks(
	markdown: string,
	{keepLineBreaks = false, videos}: {keepLineBreaks?: boolean; videos?: ReadonlySet<string>} = {},
): Array<DocumentBlock> {
	return blocksOf(markdown, {keepLineBreaks, videos}, !keepLineBreaks);
}

interface BlockOptions {
	keepLineBreaks: boolean;
	videos?: ReadonlySet<string>;
}

function blocksOf(markdown: string, options: BlockOptions, readFrontMatter: boolean): Array<DocumentBlock> {
	const {keepLineBreaks, videos} = options;
	const lines = markdown.replace(/\r\n?/g, '\n').split('\n');
	const out: Array<DocumentBlock> = [];
	const front = readFrontMatter ? frontMatter(lines) : null;
	if (front?.rows.length) out.push({kind: 'facts', rows: front.rows});
	let buffer: Array<string> = [];
	let tasks: Extract<DocumentBlock, {kind: 'tasks'}> | null = null;
	let fence: string | null = null;
	/** Whether the line before was only pictures, so a block of badge lines stays one row. */
	let previousPictures = false;
	const flush = () => {
		const text = buffer.join('\n');
		if (text.trim()) out.push({kind: 'text', text});
		buffer = [];
	};
	const endTasks = () => {
		if (tasks) out.push(tasks);
		tasks = null;
	};
	let html: {lines: Array<string>; comment: boolean} | null = null;
	const endHtml = () => {
		const text = html && !html.comment ? html.lines.join('\n').trim() : '';
		if (text) out.push({kind: 'html', html: text});
		html = null;
	};
	for (let at = front?.rest ?? 0; at < lines.length; at++) {
		const line = lines[at];
		// Raw HTML runs to the next blank line (a comment to its end), as in CommonMark.
		if (html) {
			if (html.comment ? true : line.trim() !== '') {
				html.lines.push(line);
				// A comment shows nothing.
				if (html.comment && line.includes('-->')) html = null;
				continue;
			}
			endHtml();
		}
		const marker = FENCE_LINE.exec(line)?.[1];
		if (fence) {
			buffer.push(line);
			if (marker && marker[0] === fence[0] && marker.length >= fence.length) fence = null;
			continue;
		}
		if (marker) {
			endTasks();
			fence = marker;
			buffer.push(line);
			continue;
		}
		const task = TASK_LINE.exec(line);
		if (task) {
			flush();
			tasks ??= {kind: 'tasks', items: []};
			tasks.items.push({
				done: task[2] !== ' ',
				text: inlineHtmlToMarkdown(task[3]),
				depth: Math.min(3, Math.floor(task[1].length / 2)),
			});
			continue;
		}
		if (tasks && line.trim() && /^\s{2,}\S/.test(line)) {
			// A wrapped task line.
			const last = tasks.items[tasks.items.length - 1];
			last.text = `${last.text} ${line.trim()}`;
			continue;
		}
		endTasks();
		if (videos?.has(line.trim())) {
			flush();
			out.push({kind: 'video', src: line.trim()});
			previousPictures = false;
			continue;
		}
		if (RULE_LINE.test(line)) {
			flush();
			out.push({kind: 'rule'});
			continue;
		}
		// A table: drawn here when it holds pictures, else left to the Markdown renderer as written.
		const align = line.includes('|') && at + 1 < lines.length ? delimiterRow(lines[at + 1]) : null;
		const header = align ? tableCells(line) : null;
		if (align && header && header.length === align.length) {
			let end = at + 2;
			while (end < lines.length && lines[end].includes('|') && lines[end].trim()) end++;
			const rows = lines.slice(at + 2, end).map(tableCells);
			if ([header, ...rows].some((row) => row.some(hasPicture))) {
				flush();
				const width = align.length;
				out.push({
					kind: 'table',
					align,
					header,
					rows: rows.map((row) => Array.from({length: width}, (_, column) => row[column] ?? '')),
				});
			} else buffer.push(...lines.slice(at, end));
			previousPictures = false;
			at = end - 1;
			continue;
		}
		const tag = HTML_START.exec(line)?.[1]?.toLowerCase();
		// A folded section keeps what is inside it, across blank lines, as blocks of its own.
		const detailsTo = tag === 'details' && !/^ {0,3}<\//.test(line) ? detailsEnd(lines, at) : -1;
		if (detailsTo >= 0) {
			flush();
			out.push(detailsBlock(lines.slice(at, detailsTo + 1).join('\n'), options));
			previousPictures = false;
			at = detailsTo;
			continue;
		}
		if ((tag && HTML_BLOCK_TAGS.has(tag)) || /^ {0,3}<!--/.test(line)) {
			flush();
			html = {lines: [line], comment: /^ {0,3}<!--/.test(line)};
			if (html.comment && line.includes('-->')) html = null;
			continue;
		}
		const pictures = pictureLine(line);
		if (pictures) {
			flush();
			const last = out[out.length - 1];
			if (last?.kind === 'pictures' && previousPictures) last.items.push(...pictures);
			else out.push({kind: 'pictures', items: pictures});
			previousPictures = true;
			continue;
		}
		previousPictures = false;
		// Pictures inside a line of text (`Design: ![mock](…)`, `Before <img …>`) stand on their own
		// between its words, as chat Markdown draws no pictures.
		const parts = (line.includes('!') || line.includes('<')) && !/^\s{4,}/.test(line) ? pictureParts(line) : [];
		if (parts.some((part) => part.kind === 'picture')) {
			for (const part of parts) {
				if (part.kind === 'text') {
					// A bullet that only held the picture leaves nothing behind.
					if (!/^\s*(?:[-*+]|\d+[.)])?\s*$/.test(part.text)) buffer.push(part.text.trim());
					continue;
				}
				flush();
				const last = out[out.length - 1];
				if (last?.kind === 'pictures' && previousPictures) last.items.push(part.picture);
				else out.push({kind: 'pictures', items: [part.picture]});
				previousPictures = true;
			}
			previousPictures = false;
			continue;
		}
		// Inline HTML GitHub allows (`<sub>`, `<b>`, `<kbd>`, `<br>`) as the Markdown chat draws; not in indented code.
		const written = /^\s{4,}/.test(line) ? line : inlineHtmlToMarkdown(line);
		const heading = HEADING_LINE.exec(written);
		if (heading && heading[1].length > 4) {
			buffer.push(`#### ${written.slice(heading[0].length)}`);
			continue;
		}
		const previous = buffer[buffer.length - 1];
		const joins =
			!keepLineBreaks &&
			previous !== undefined &&
			previous.trim() !== '' &&
			line.trim() !== '' &&
			!BLOCK_START.test(line) &&
			!/^\s{4,}/.test(line) &&
			!HEADING_LINE.test(previous) &&
			!/^\s{0,3}(\||```|~~~)/.test(previous) &&
			!/( {2}|\\)$/.test(previous);
		if (joins) buffer[buffer.length - 1] = `${previous.trimEnd()} ${written.trim()}`;
		else buffer.push(written);
	}
	endHtml();
	endTasks();
	flush();
	return out;
}
